import React, { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { Check, X, Eye, AlertCircle, Filter, Minus, History, ChevronDown, ChevronRight, ClipboardCheck, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import type { Timesheet } from '@/types/timesheet';
import { toast } from 'sonner';
import { AnomalyIndicator } from '@/components/AnomalyIndicator';
import { AnomalyDetectionService } from '@/services/anomalyDetectionService';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import { AuditHistoryDialog } from '@/components/AuditHistory';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { EmployeeImage } from '@/components/EmployeeImage';
import { ApprovalEmptyState } from '@/components/ApprovalEmptyState';
import { SmartBanner, InlineBadge, WeeklyInsightCard, PatternReviewDialog } from '@/components/ApprovalAutomation';
import { useApprovalAutomation } from '@/hooks/use-approval-automation';
import { rejectionService } from '@/services/rejectionService';
import { useDataSource } from '@/contexts/DataSourceContext';
import { calculateEntryVariance, getVarianceBadgeVariant } from '@/services/varianceCalculationService';
import { BookingDetailsModal } from '@/components/ScheduledBookings';
import { approvalsApi } from '@/services/approvalsApi';
import { AbsenceIndicator } from '@/components/AbsenceBadge';
import type { AbsenceType } from '@/components/AbsenceBadge';
import { PolicyViolationAlert } from '@/components/timesheet/PolicyViolationAlert';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import { DetectedIssuesPanel } from '@/components/DetectedIssuesPanel';
import { consolidateAnomalies, shouldConsolidate } from '@/services/anomalyConsolidationService';
import type { ConsolidatedAnomaly } from '@/services/anomalyConsolidationService';
import { getViolationsForEmployee } from '@/data/mockPolicyViolations';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { calculateViolationsForTimesheets, getHighestSeverity } from '@/services/policyViolationService';
import { PageHeader } from '@/components/blocks/page-header';

// Helper function to generate a random photo URL for employees without proper avatars
const getDemoPhotoUrl = (name: string, id: number): string => {
  const seed = encodeURIComponent(name.replace(/\s+/g, '-'));
  return `https://picsum.photos/seed/${seed}-${id}/300/300`;
};

// Helper function to check if URL is an Evo avatar (not from dicebear or other non-Evo sources)
const isEvoAvatarUrl = (url: string): boolean => {
  const trimmedUrl = url.trim().toLowerCase();
  if (trimmedUrl.includes('dicebear')) {
    return false;
  }
  return true;
};

// Helper function to get photo URL - uses Evo avatar if available, otherwise random image
const getPhotoUrl = (name: string, id: number, photoUrl?: string | null): string => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return getDemoPhotoUrl(name, id);
};

// Constants
const MAX_SELECTION_LIMIT = 500;
const BULK_CONFIRMATION_THRESHOLD = 50;

export function ApprovalDashboard() {
  const { timesheets, employees, projects, currentEmployee, updateTimesheet } = useTimesheetForScreen('approvals');
  const { getScreenMode } = useDataSource();
  const { canViewApprovalDashboard, canViewApprovals, canApproveReject, canBulkApprove } = usePermissions();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [viewingTimesheet, setViewingTimesheet] = useState<Timesheet | null>(null);
  const [rejectingTimesheet, setRejectingTimesheet] = useState<Timesheet | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [anomalyFilter, setAnomalyFilter] = useState<string>('all');
  const [overdueFilter, setOverdueFilter] = useState<boolean>(false);
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState<'approve' | null>(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<number>>(new Set());
  const [viewingBookingDetails, setViewingBookingDetails] = useState<any | null>(null);
  
  // Sorting state
  type SortField = 'employee' | 'department' | 'weekEnding' | 'totalHours' | 'violations' | 'status';
  type SortDirection = 'asc' | 'desc' | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null (default)
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      // New field, start with asc
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Get sort icon for a field
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 text-muted-foreground" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="h-3.5 w-3.5 ml-1 text-primary" />;
    }
    return <ArrowDown className="h-3.5 w-3.5 ml-1 text-primary" />;
  };
  
  // Server-side pending approvals state (for live mode)
  const [serverPendingTimesheets, setServerPendingTimesheets] = useState<Timesheet[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  
  // Get current screen mode
  const screenMode = getScreenMode('approvals');

  // Get organization settings for policy violation calculations
  const { settings: orgSettings, loading: settingsLoading } = useOrganisationSettings('approvals');

  // Approval Automation
  const [reviewingPattern, setReviewingPattern] = useState<string | null>(null);
  const automation = useApprovalAutomation(timesheets, employees, currentEmployee.id);

  // Detect patterns on mount and when timesheets change
  useEffect(() => {
    automation.detectPatterns();
  }, [timesheets.length]);

  // Fetch pending approvals from API in live mode
  useEffect(() => {
    if (screenMode === 'live') {
      setIsLoadingPending(true);
      approvalsApi.getPendingApprovals()
        .then(setServerPendingTimesheets)
        .catch((error) => {
          console.error('Failed to fetch pending approvals:', error);
          toast.error('Failed to load pending approvals');
        })
        .finally(() => setIsLoadingPending(false));
    }
  }, [screenMode]);

  // Listen for company-changed event to refetch data when switching companies
  // This ensures data is fetched from the correct integration source (local DB vs external API)
  useEffect(() => {
    const handleCompanyChange = () => {
      if (screenMode === 'live') {
        setIsLoadingPending(true);
        approvalsApi.getPendingApprovals()
          .then(setServerPendingTimesheets)
          .catch((error) => {
            console.error('Failed to fetch pending approvals after company change:', error);
            toast.error('Failed to load pending approvals');
          })
          .finally(() => setIsLoadingPending(false));
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, [screenMode]);

  // Helper function to refresh pending approvals in live mode
  const refreshPendingApprovals = async () => {
    if (screenMode === 'live') {
      try {
        const approvals = await approvalsApi.getPendingApprovals();
        setServerPendingTimesheets(approvals);
      } catch (error) {
        console.error('Failed to refresh pending approvals:', error);
      }
    }
  };

  // Get timesheets assigned to current user as approver - workflow-aware filtering
  // In live mode, use server-side filtered data; in demo mode, use client-side filtering
  const pendingTimesheets = useMemo(() => {
    if (screenMode === 'live') {
      // Use server-side filtered data in live mode
      return serverPendingTimesheets;
    }
    // Client-side filtering for demo mode
    return timesheets.filter(ts => {
      // Only show submitted (pending) timesheets
      if (ts.status !== 'submitted') return false;
      
      // Show timesheets assigned to current user as approver
      const isAssignedToMe = ts.approverId === currentEmployee.id;
      
      return isAssignedToMe;
    });
  }, [screenMode, serverPendingTimesheets, timesheets, currentEmployee]);

  // Calculate anomalies for all timesheets
  // Pass organization settings to use settings-based limits instead of hardcoded defaults
  const timesheetAnomalies = useMemo(() => {
    const anomalyMap = new Map<number, AnomalyDetectionResult>();
    
    pendingTimesheets.forEach(timesheet => {
      const employee = employees.find(e => e.id === timesheet.employeeId);
      if (!employee) return;

      // Calculate historical data for the employee
      const historicalData = AnomalyDetectionService.calculateHistoricalData(
        timesheet.employeeId,
        timesheets
      );

      // Detect anomalies - pass orgSettings to use settings-based limits
      const anomalyResult = AnomalyDetectionService.detectAnomalies(
        timesheet,
        employee,
        historicalData,
        timesheets,
        orgSettings
      );

      anomalyMap.set(timesheet.id, anomalyResult);
    });

    return anomalyMap;
  }, [pendingTimesheets, employees, timesheets, orgSettings]);

  // Calculate policy violations for all timesheets
  // In live mode: use backend settings to calculate violations
  // In demo mode: use mock data
  const timesheetPolicyViolations = useMemo(() => {
    const violationsMap = new Map<number, PolicyViolation[]>();
    
    if (screenMode === 'live') {
      // Live mode: calculate violations from backend settings
      if (settingsLoading || !orgSettings) {
        return violationsMap;
      }
      return calculateViolationsForTimesheets(pendingTimesheets, orgSettings);
    } else {
      // Demo mode: use mock data
      pendingTimesheets.forEach(ts => {
        const violations = getViolationsForEmployee(ts.employeeId);
        if (violations.length > 0) {
          violationsMap.set(ts.id, violations);
        }
      });
      return violationsMap;
    }
  }, [pendingTimesheets, orgSettings, settingsLoading, screenMode]);

  // Helper function to calculate timesheet total hours
  const calculateTimesheetTotal = (timesheet: Timesheet) => {
    return timesheet.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
  };

  // Apply filters
  const filteredTimesheets = useMemo(() => {
    console.log('🔍 Applying filters:', {
      anomalyFilter,
      departmentFilter,
      overdueFilter,
      searchTerm,
      totalPending: pendingTimesheets.length
    });

    const filtered = pendingTimesheets.filter(ts => {
      const employee = employees.find(e => e.id === ts.employeeId);
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];
      
      const matchesSearch = searchTerm === '' || 
        ts.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDepartment = departmentFilter === 'all' || 
        employee?.department === departmentFilter;

      // Check if timesheet has any issues (anomalies or policy violations)
      const hasIssues = anomalyResult?.hasAnomalies || policyViolations.length > 0;
      const matchesAnomaly = anomalyFilter === 'all' || 
        (anomalyFilter === 'with-anomalies' && hasIssues) ||
        (anomalyFilter === 'clean' && !hasIssues);

      // Check overdue filter
      const daysSinceSubmission = ts.submittedAt 
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      const isOverdue = daysSinceSubmission > 3;
      const matchesOverdue = !overdueFilter || isOverdue;

      const passes = matchesSearch && matchesDepartment && matchesAnomaly && matchesOverdue;

      return passes;
    });

    console.log(`✅ Filtered results: ${filtered.length} of ${pendingTimesheets.length} timesheets`);
    
    // Apply sorting
    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let compareValue = 0;
        
        switch (sortField) {
          case 'employee':
            compareValue = a.employeeName.localeCompare(b.employeeName);
            break;
          case 'department': {
            const deptA = employees.find(e => e.id === a.employeeId)?.department || '';
            const deptB = employees.find(e => e.id === b.employeeId)?.department || '';
            compareValue = deptA.localeCompare(deptB);
            break;
          }
          case 'weekEnding':
            compareValue = a.weekEnding.getTime() - b.weekEnding.getTime();
            break;
          case 'totalHours': {
            const totalA = calculateTimesheetTotal(a);
            const totalB = calculateTimesheetTotal(b);
            compareValue = totalA - totalB;
            break;
          }
          case 'violations': {
            const violationsA = timesheetPolicyViolations.get(a.id)?.length || 0;
            const violationsB = timesheetPolicyViolations.get(b.id)?.length || 0;
            compareValue = violationsA - violationsB;
            break;
          }
          case 'status': {
            const statusOrder = { 'pending': 0, 'approved': 1, 'rejected': 2 };
            compareValue = (statusOrder[a.status as keyof typeof statusOrder] || 0) - 
                          (statusOrder[b.status as keyof typeof statusOrder] || 0);
            break;
          }
        }
        
        return sortDirection === 'asc' ? compareValue : -compareValue;
      });
    }
    
    return filtered;
  }, [pendingTimesheets, searchTerm, departmentFilter, anomalyFilter, overdueFilter, employees, timesheetAnomalies, timesheetPolicyViolations, sortField, sortDirection]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const pending = pendingTimesheets.length;
    const overdue = pendingTimesheets.filter(ts => {
      const daysSinceSubmission = ts.submittedAt 
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      return daysSinceSubmission > 3;
    }).length;
    
    const totalHours = pendingTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }, 0);
    }, 0);

    // Calculate anomaly and policy violation stats
    let withAnomalies = 0;
    let highSeverity = 0;
    let mediumSeverity = 0;
    let lowSeverity = 0;

    pendingTimesheets.forEach(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];
      
      // Check for anomalies or policy violations
      const hasAnomalies = anomalyResult?.hasAnomalies;
      const hasPolicyViolations = policyViolations.length > 0;
      
      if (hasAnomalies || hasPolicyViolations) {
        withAnomalies++;
        
        // Check anomaly severities
        const hasHighAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'high') ?? false;
        const hasMediumAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'medium') ?? false;
        const hasLowAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'low') ?? false;
        
        // Check policy violation severities (error=high, warning=medium, info=low)
        const hasHighViolation = policyViolations.some(v => v.severity === 'error');
        const hasMediumViolation = policyViolations.some(v => v.severity === 'warning');
        const hasLowViolation = policyViolations.some(v => v.severity === 'info');
        
        // Combine both sources
        if (hasHighAnomaly || hasHighViolation) highSeverity++;
        else if (hasMediumAnomaly || hasMediumViolation) mediumSeverity++;
        else if (hasLowAnomaly || hasLowViolation) lowSeverity++;
      }
    });

    return { pending, overdue, totalHours, withAnomalies, highSeverity, mediumSeverity, lowSeverity };
  }, [pendingTimesheets, timesheetAnomalies, timesheetPolicyViolations]);

  const departments = useMemo(() => {
    const depts = new Set(employees.map(e => e.department));
    return Array.from(depts);
  }, [employees]);

  // Calculate checkbox state (checked, unchecked, or indeterminate)
  const selectAllState = useMemo(() => {
    if (filteredTimesheets.length === 0) return 'unchecked';
    
    const selectedFilteredCount = filteredTimesheets.filter(ts => 
      selectedIds.has(ts.id)
    ).length;
    
    if (selectedFilteredCount === 0) return 'unchecked';
    if (selectedFilteredCount === filteredTimesheets.length) return 'checked';
    return 'indeterminate';
  }, [selectedIds, filteredTimesheets]);

  // Clear selection when filters change significantly
  useEffect(() => {
    // Keep only selections that are still in filtered results
    const validIds = new Set(
      Array.from(selectedIds).filter(id => 
        filteredTimesheets.some(ts => ts.id === id)
      )
    );
    if (validIds.size !== selectedIds.size) {
      setSelectedIds(validIds);
    }
  }, [filteredTimesheets]);

  const toggleSelection = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      // Check selection limit
      if (newSet.size >= MAX_SELECTION_LIMIT) {
        toast.error(
          `Selection limit reached (${MAX_SELECTION_LIMIT} items maximum)`,
          {
            description: 'Please process current selection before selecting more items.',
            duration: 5000,
          }
        );
        return;
      }
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectAllState === 'checked') {
      // Deselect all
      setSelectedIds(new Set());
      toast.info('All items deselected');
    } else {
      // Select all (or remaining items if indeterminate)
      const idsToSelect = filteredTimesheets.map(ts => ts.id);
      
      // Check if selection would exceed limit
      if (idsToSelect.length > MAX_SELECTION_LIMIT) {
        toast.error(
          `Cannot select ${idsToSelect.length} items`,
          {
            description: `Selection limit is ${MAX_SELECTION_LIMIT} items. Please use filters to reduce the number of items.`,
            duration: 6000,
          }
        );
        return;
      }

      // Warn if selecting large number
      if (idsToSelect.length > 100) {
        toast.warning(
          `Selecting ${idsToSelect.length} items`,
          {
            description: 'Large selections may take longer to process.',
            duration: 4000,
          }
        );
      }

      setSelectedIds(new Set(idsToSelect));
      
      // Announce selection for screen readers
      const message = `${idsToSelect.length} of ${filteredTimesheets.length} timesheets selected`;
      toast.success(message);
    }
  };

  const approveTimesheet = async (timesheet: Timesheet) => {
    if (screenMode === 'live') {
      try {
        const response = await approvalsApi.approveTimesheet(timesheet.id);
        
        // Update the timesheet in context with the response from the server
        if (response.timesheet) {
          const updatedTimesheet: Timesheet = {
            ...timesheet,
            status: response.timesheet.status as Timesheet['status'],
            approvedAt: response.timesheet.approvedAt ? new Date(response.timesheet.approvedAt) : undefined,
            approverId: response.timesheet.approverId,
            approverName: response.timesheet.approverName,
            currentStageName: response.timesheet.currentStageName,
            currentStageOrder: response.timesheet.currentStageOrder,
            totalStages: response.timesheet.totalStages,
          };
          updateTimesheet(updatedTimesheet);
        }
        
        // Show appropriate message based on workflow status
        if (response.workflowCompleted && response.finalStatus === 'approved') {
          toast.success(`Timesheet for ${timesheet.employeeName} fully approved`);
        } else if (response.advancedToNextStage) {
          toast.success(`Approved stage for ${timesheet.employeeName}. ${response.newStageName ? `Now at: ${response.newStageName}` : ''}`);
        } else {
          toast.success(`Approved timesheet for ${timesheet.employeeName}`);
        }
        
        // Refresh pending approvals list
        refreshPendingApprovals();
      } catch (error) {
        console.error('Failed to approve timesheet:', error);
        toast.error('Failed to approve timesheet');
      }
    } else {
      // Existing demo mode logic
      const updated = {
        ...timesheet,
        status: 'approved' as const,
        approvedAt: new Date(),
        approverId: currentEmployee.id,
        approverName: currentEmployee.name
      };
      updateTimesheet(updated);
      toast.success(`Approved timesheet for ${timesheet.employeeName}`);
    }
  };

  const handleBulkApproveClick = () => {
    const toApprove = filteredTimesheets.filter(ts => selectedIds.has(ts.id));
    
    // Show confirmation for large selections or items with anomalies
    const itemsWithAnomalies = toApprove.filter(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      return anomalyResult?.hasAnomalies;
    });

    if (toApprove.length >= BULK_CONFIRMATION_THRESHOLD || itemsWithAnomalies.length > 0) {
      setPendingBulkAction('approve');
      setShowBulkConfirmDialog(true);
    } else {
      executeBulkApprove();
    }
  };

  const executeBulkApprove = () => {
    const toApprove = filteredTimesheets.filter(ts => selectedIds.has(ts.id));
    
    // Show progress for large batches
    if (toApprove.length > 20) {
      toast.loading(`Approving ${toApprove.length} timesheets...`, {
        id: 'bulk-approve',
      });
    }

    toApprove.forEach(ts => approveTimesheet(ts));
    setSelectedIds(new Set());
    
    // Dismiss loading toast and show success
    toast.dismiss('bulk-approve');
    toast.success(`Successfully approved ${toApprove.length} timesheets`);
    
    setShowBulkConfirmDialog(false);
    setPendingBulkAction(null);
  };

  // Get selected timesheets for confirmation dialog
  const selectedTimesheets = useMemo(() => {
    return filteredTimesheets.filter(ts => selectedIds.has(ts.id));
  }, [filteredTimesheets, selectedIds]);

  const selectedWithAnomalies = useMemo(() => {
    return selectedTimesheets.filter(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      return anomalyResult?.hasAnomalies;
    });
  }, [selectedTimesheets, timesheetAnomalies]);

  const rejectTimesheet = async () => {
    if (!rejectingTimesheet || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      if (screenMode === 'live') {
        // Use dedicated API endpoint in live mode
        await approvalsApi.rejectTimesheet(rejectingTimesheet.id, rejectionReason);
        toast.success(`Rejected timesheet for ${rejectingTimesheet.employeeName}`);
        // Refresh pending approvals list
        refreshPendingApprovals();
      } else {
        // Existing demo mode logic
        const updated = {
          ...rejectingTimesheet,
          status: 'rejected' as const,
          rejectedAt: new Date(),
          rejectionReason,
          approverId: currentEmployee.id,
          approverName: currentEmployee.name
        };
        updateTimesheet(updated);

        // Record rejection in RejectionRecord table
        rejectionService.setMode(false);
        
        await rejectionService.recordRejection(
          rejectingTimesheet,
          rejectionReason,
          currentEmployee.id,
          currentEmployee.name,
          projects,
          [], // costCentres - not needed for basic rejection tracking
          {
            approverRole: currentEmployee.role || 'manager'
          }
        );

        toast.success(`Rejected timesheet for ${rejectingTimesheet.employeeName}`);
      }
    } catch (error) {
      console.error('Failed to reject timesheet:', error);
      toast.error('Failed to reject timesheet');
    } finally {
      // Always close the modal and clear the reason so the user is not stuck
      setRejectingTimesheet(null);
      setRejectionReason('');
    }
  };

  const toggleEntryExpansion = (entryId: number) => {
    const newSet = new Set(expandedEntryIds);
    if (newSet.has(entryId)) {
      newSet.delete(entryId);
    } else {
      newSet.add(entryId);
    }
    setExpandedEntryIds(newSet);
  };

  const shouldShowExpansionIcon = (description: string) => {
    return description && description.length > 100;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Approvals"
        subtitle="Review and approve team member timesheets"
        size={36}
        showBreadcrumb={false}
        icon={<ClipboardCheck />}
      />

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Pending Approvals</CardTitle>
          <div className="mt-4">
            {/* Single Filters Row */}
            <div className="flex items-center gap-4">
              <div className="w-[300px]">
                <Input
                  placeholder="Search by employee name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={anomalyFilter} onValueChange={setAnomalyFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Issues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="with-anomalies">With Issues</SelectItem>
                  <SelectItem value="clean">No Issues</SelectItem>
                </SelectContent>
              </Select>
              {(searchTerm || departmentFilter !== 'all' || anomalyFilter !== 'all' || overdueFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setDepartmentFilter('all');
                    setAnomalyFilter('all');
                    setOverdueFilter(false);
                    toast.info('Filters cleared');
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Clear Filters
                </Button>
              )}
              <Button
                onClick={handleBulkApproveClick}
                disabled={selectedIds.size === 0 || !canBulkApprove()}
                title={!canBulkApprove() ? "You don't have permission to bulk approve" : undefined}
                size="sm"
                className="ml-auto"
              >
                <Check className="h-4 w-4 mr-2" />
                Approve Selected ({selectedIds.size})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Header */}
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg font-medium text-sm">
              <div className="w-8">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Checkbox
                          checked={selectAllState === 'indeterminate' ? 'indeterminate' : selectAllState === 'checked'}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all timesheets"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {selectAllState === 'checked' && 'Deselect all'}
                        {selectAllState === 'unchecked' && 'Select all'}
                        {selectAllState === 'indeterminate' && 'Select all remaining'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div 
                className="flex-1 flex items-center cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('employee')}
              >
                Employee
                {getSortIcon('employee')}
                {selectedIds.size > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    ({selectedIds.size} of {filteredTimesheets.length} selected)
                  </span>
                )}
              </div>
              <div 
                className="w-32 flex items-center cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('department')}
              >
                Department
                {getSortIcon('department')}
              </div>
              <div 
                className="w-32 flex items-center cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('weekEnding')}
              >
                Week Ending
                {getSortIcon('weekEnding')}
              </div>
              <div 
                className="w-24 text-right flex items-center justify-end cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('totalHours')}
              >
                Total Hours
                {getSortIcon('totalHours')}
              </div>
              <div 
                className="w-32 text-center flex items-center justify-center cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('violations')}
              >
                Violations
                {getSortIcon('violations')}
              </div>
              <div 
                className="w-32 text-center flex items-center justify-center cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('status')}
              >
                Status
                {getSortIcon('status')}
              </div>
              <div className="w-56 text-right">Actions</div>
            </div>

            {/* Timesheet Rows */}
            {pendingTimesheets.length === 0 ? (
              <ApprovalEmptyState />
            ) : filteredTimesheets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No timesheets match your current filters
              </div>
            ) : (
              filteredTimesheets.map(timesheet => {
                const employee = employees.find(e => e.id === timesheet.employeeId);
                const total = calculateTimesheetTotal(timesheet);
                const anomalyResult = timesheetAnomalies.get(timesheet.id);
                const policyViolations = timesheetPolicyViolations.get(timesheet.id) || [];
                const daysSinceSubmission = timesheet.submittedAt 
                  ? Math.floor((Date.now() - timesheet.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
                  : 0;
                const isOverdue = daysSinceSubmission > 3;

                return (
                  <div
                    key={timesheet.id}
                    className={`flex items-center gap-4 p-3 rounded-lg border hover:bg-muted/50 ${
                      isOverdue ? 'border-orange-300 bg-orange-50/50 dark:bg-orange-950/20' : ''
                    }`}
                  >
                    <div className="w-8">
                      <Checkbox
                        checked={selectedIds.has(timesheet.id)}
                        onCheckedChange={() => toggleSelection(timesheet.id)}
                      />
                    </div>
                    <div className="flex-1 flex items-center gap-3">
                      <EmployeeImage 
                        name={timesheet.employeeName}
                        photoUrl={getPhotoUrl(timesheet.employeeName, timesheet.employeeId, employee?.photoUrl)}
                        size="md"
                      />
                      <div className="font-medium">{timesheet.employeeName}</div>
                    </div>
                    <div className="w-32 text-sm">{employee?.department}</div>
                    <div className="w-32 text-sm">
                      {format(timesheet.weekEnding, 'dd MMM yyyy')}
                    </div>
                    <div className="w-24 text-right font-semibold">
                      {total.toFixed(1)}h
                    </div>
                    <div className="w-32 flex justify-center">
                      {policyViolations.length > 0 ? (
                        <PolicyViolationAlert violations={policyViolations} compact />
                      ) : (
                        <span className="text-xs text-muted-foreground">None</span>
                      )}
                    </div>
                    <div className="w-32 flex justify-center gap-1">
                      {anomalyResult && (
                        <AnomalyIndicator 
                          anomalyResult={anomalyResult}
                          size="sm"
                        />
                      )}
                      {isOverdue && (
                        <Badge variant="destructive" className="text-xs">
                          Overdue
                        </Badge>
                      )}
                    </div>
                    <div className="w-56 flex justify-end gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewingTimesheet(timesheet)}
                            className="px-2"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View Timesheet</p>
                        </TooltipContent>
                      </Tooltip>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => approveTimesheet(timesheet)}
                        disabled={!canApproveReject()}
                        title={!canApproveReject() ? "You don't have permission to approve timesheets" : undefined}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setRejectingTimesheet(timesheet)}
                        disabled={!canApproveReject()}
                        title={!canApproveReject() ? "You don't have permission to reject timesheets" : undefined}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Timesheet Dialog */}
      <Dialog open={!!viewingTimesheet} onOpenChange={() => setViewingTimesheet(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {viewingTimesheet && (
                <EmployeeAvatar 
                  name={viewingTimesheet.employeeName}
                  photoUrl={getPhotoUrl(viewingTimesheet.employeeName, viewingTimesheet.employeeId, employees.find(e => e.id === viewingTimesheet.employeeId)?.photoUrl)}
                  size="lg"
                />
              )}
              <div className="flex items-center gap-3">
                <span>Timesheet - {viewingTimesheet?.employeeName}</span>
                {viewingTimesheet && timesheetAnomalies.get(viewingTimesheet.id) && (
                  <AnomalyIndicator 
                    anomalyResult={timesheetAnomalies.get(viewingTimesheet.id)!}
                    size="md"
                  />
                )}
              </div>
            </DialogTitle>
            <DialogDescription className="ml-[60px]">
              Week ending {viewingTimesheet && format(viewingTimesheet.weekEnding, 'dd MMM yyyy')}
            </DialogDescription>
          </DialogHeader>
          {viewingTimesheet && (
            <div className="space-y-4">
              {/* Policy Violations */}
              {(() => {
                const violations = timesheetPolicyViolations.get(viewingTimesheet.id) || [];
                return violations.length > 0 ? (
                  <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 mb-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        Policy Violations
                        <Badge variant="outline" className="ml-auto">
                          {violations.length} {violations.length === 1 ? 'violation' : 'violations'}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <PolicyViolationAlert violations={violations} />
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              {/* Anomaly Details - Three-Level Expandable/Collapsible */}
              {timesheetAnomalies.get(viewingTimesheet.id)?.hasAnomalies && (() => {
                const anomalies = timesheetAnomalies.get(viewingTimesheet.id)?.anomalies || [];
                
                return (
                  <DetectedIssuesPanel 
                    anomalies={anomalies}
                    defaultExpanded={true}
                  />
                );
              })()}
              {/* Calculate daily totals */}
              {(() => {
                const dailyTotals = {
                  monday: 0,
                  tuesday: 0,
                  wednesday: 0,
                  thursday: 0,
                  friday: 0,
                  saturday: 0,
                  sunday: 0,
                };
                
                viewingTimesheet.entries.forEach(entry => {
                  dailyTotals.monday += entry.hours.monday;
                  dailyTotals.tuesday += entry.hours.tuesday;
                  dailyTotals.wednesday += entry.hours.wednesday;
                  dailyTotals.thursday += entry.hours.thursday;
                  dailyTotals.friday += entry.hours.friday;
                  dailyTotals.saturday += entry.hours.saturday;
                  dailyTotals.sunday += entry.hours.sunday;
                });
                
                const grandTotal = Object.values(dailyTotals).reduce((sum, h) => sum + h, 0);
                
                // Store totals for use in the table
                viewingTimesheet._dailyTotals = dailyTotals;
                viewingTimesheet._grandTotal = grandTotal;
                
                return null;
              })()}
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-2 text-left">Project</th>
                      <th className="p-2 text-left">Cost Centre</th>
                      <th className="p-2 text-left">Description</th>
                      <th className="p-2 text-center">Mon</th>
                      <th className="p-2 text-center">Tue</th>
                      <th className="p-2 text-center">Wed</th>
                      <th className="p-2 text-center">Thu</th>
                      <th className="p-2 text-center">Fri</th>
                      <th className="p-2 text-center bg-muted">Sat</th>
                      <th className="p-2 text-center bg-muted">Sun</th>
                      <th className="p-2 text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingTimesheet.entries.map(entry => {
                      const project = projects.find(p => p.id === entry.projectId);
                      const costCentre = project?.costCentres.find(cc => cc.id === entry.costCentreId);
                      const total = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);
                      const isExpanded = expandedEntryIds.has(entry.id);
                      const showExpansion = shouldShowExpansionIcon(entry.description);
                      const truncatedDescription = entry.description.length > 100 
                        ? entry.description.substring(0, 100) + '...' 
                        : entry.description;
                      
                      // Check if this is an absence entry from People HR
                      const isAbsence = entry.sourceIntegration === 'people-hr';
                      const absenceType: AbsenceType | null = isAbsence 
                        ? (entry.description?.toLowerCase().replace(' ', '-') as AbsenceType)
                        : null;
                      
                      // Calculate variance for scheduled bookings
                      const variance = entry.isFromSchedule && !entry.isDismissed
                        ? calculateEntryVariance(entry)
                        : null;
                      
                      return (
                        <React.Fragment key={entry.id}>
                          <tr className={`border-b hover:bg-muted/30 transition-colors ${isAbsence ? 'bg-purple-50/30' : ''}`}>
                            <td className="p-2">
                              <div className="flex flex-col gap-1">
                                <div className="text-xs truncate max-w-[200px]">
                                  {project ? `${project.name} (${project.code})` : '-'}
                                </div>
                                {isAbsence && <AbsenceIndicator />}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="text-xs truncate max-w-[150px]">
                                {costCentre ? `${costCentre.name} (${costCentre.code})` : '-'}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 max-w-[280px]">
                                  {showExpansion && (
                                    <button
                                      onClick={() => toggleEntryExpansion(entry.id)}
                                      className="flex-shrink-0 hover:bg-muted rounded p-0.5 transition-colors"
                                      aria-label={isExpanded ? 'Collapse description' : 'Expand description'}
                                      aria-expanded={isExpanded}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                      )}
                                    </button>
                                  )}
                                  <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="text-xs flex-1 min-w-0">
                                          {!isExpanded && showExpansion ? (
                                            <span className="line-clamp-1">{truncatedDescription}</span>
                                          ) : (
                                            <span className={showExpansion ? '' : 'line-clamp-2'}>{entry.description || '-'}</span>
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      {entry.description && entry.description.length > 50 && (
                                        <TooltipContent side="top" className="max-w-md">
                                          <p className="text-xs whitespace-pre-wrap">{entry.description}</p>
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </div>
                            </td>
                            <td className="p-2 text-center">{entry.hours.monday > 0 ? entry.hours.monday.toFixed(2) : '-'}</td>
                            <td className="p-2 text-center">{entry.hours.tuesday > 0 ? entry.hours.tuesday.toFixed(2) : '-'}</td>
                            <td className="p-2 text-center">{entry.hours.wednesday > 0 ? entry.hours.wednesday.toFixed(2) : '-'}</td>
                            <td className="p-2 text-center">{entry.hours.thursday > 0 ? entry.hours.thursday.toFixed(2) : '-'}</td>
                            <td className="p-2 text-center">{entry.hours.friday > 0 ? entry.hours.friday.toFixed(2) : '-'}</td>
                            <td className="p-2 text-center bg-muted/30">
                              {entry.hours.saturday > 0 && (
                                <span className="text-orange-600 font-semibold">{entry.hours.saturday.toFixed(2)}</span>
                              )}
                              {entry.hours.saturday === 0 && '-'}
                            </td>
                            <td className="p-2 text-center bg-muted/30">
                              {entry.hours.sunday > 0 && (
                                <span className="text-orange-600 font-semibold">{entry.hours.sunday.toFixed(2)}</span>
                              )}
                              {entry.hours.sunday === 0 && '-'}
                            </td>
                            <td className="p-2 text-center font-semibold">{total.toFixed(2)}</td>
                          </tr>
                          
                          {/* Variance Display for Scheduled Bookings */}
                          {entry.isFromSchedule && !entry.isDismissed && variance && Math.abs(variance.percentage) > 10 && (
                            <tr className="border-b bg-blue-50/50 dark:bg-blue-950/20">
                              <td colSpan={11} className="p-2">
                                <div className="flex items-center gap-3 text-xs">
                                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                    📅 Scheduled Booking
                                  </Badge>
                                  <span className="text-muted-foreground">
                                    Scheduled: {variance.scheduled}h
                                  </span>
                                  <span className="text-muted-foreground">|</span>
                                  <span className="text-muted-foreground">
                                    Logged: {variance.actual}h
                                  </span>
                                  <span className="text-muted-foreground">|</span>
                                  <Badge variant={getVarianceBadgeVariant(variance.severity)}>
                                    {variance.variance > 0 ? '+' : ''}{variance.variance}h ({variance.variance > 0 ? '+' : ''}{variance.percentage}%)
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 ml-auto"
                                    onClick={() => setViewingBookingDetails({
                                      ...entry,
                                      projectName: project?.name || '',
                                      costCentreName: costCentre?.name || '',
                                    })}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Details
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                          
                          {/* Dismissal Display for Dismissed Bookings */}
                          {entry.isDismissed && (
                            <tr className="border-b bg-orange-50/50 dark:bg-orange-950/20">
                              <td colSpan={11} className="p-2">
                                <div className="flex items-center gap-3 text-xs">
                                  <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                                    🚫 Dismissed Booking
                                  </Badge>
                                  <span className="text-muted-foreground">
                                    {entry.dismissalReason ? `"${entry.dismissalReason}"` : 'No reason provided'}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 ml-auto"
                                    onClick={() => setViewingBookingDetails({
                                      ...entry,
                                      projectName: project?.name || '',
                                      costCentreName: costCentre?.name || '',
                                    })}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Details
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                          
                          {isExpanded && showExpansion && (
                            <tr className="border-b bg-muted/20">
                              <td colSpan={11} className="p-3">
                                <div className="text-xs">
                                  <div className="font-medium text-muted-foreground mb-1">Full Description:</div>
                                  <div className="text-foreground whitespace-pre-wrap bg-background p-3 rounded border">
                                    {entry.description}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Daily Totals Row */}
                    {viewingTimesheet._dailyTotals && (
                      <tr className="border-t-2 border-t-primary bg-accent-teal-50/50 dark:bg-accent-teal-950/20 font-semibold">
                        <td colSpan={3} className="p-2 text-left">
                          <span className="font-bold">Daily Totals</span>
                        </td>
                        <td className="p-2 text-center">
                          {viewingTimesheet._dailyTotals.monday > 0 ? viewingTimesheet._dailyTotals.monday.toFixed(2) : '-'}
                        </td>
                        <td className="p-2 text-center">
                          {viewingTimesheet._dailyTotals.tuesday > 0 ? viewingTimesheet._dailyTotals.tuesday.toFixed(2) : '-'}
                        </td>
                        <td className="p-2 text-center">
                          {viewingTimesheet._dailyTotals.wednesday > 0 ? viewingTimesheet._dailyTotals.wednesday.toFixed(2) : '-'}
                        </td>
                        <td className="p-2 text-center">
                          {viewingTimesheet._dailyTotals.thursday > 0 ? viewingTimesheet._dailyTotals.thursday.toFixed(2) : '-'}
                        </td>
                        <td className="p-2 text-center">
                          {viewingTimesheet._dailyTotals.friday > 0 ? viewingTimesheet._dailyTotals.friday.toFixed(2) : '-'}
                        </td>
                        <td className="p-2 text-center bg-muted/30">
                          {viewingTimesheet._dailyTotals.saturday > 0 && (
                            <span className="text-orange-600 font-bold">{viewingTimesheet._dailyTotals.saturday.toFixed(2)}</span>
                          )}
                          {viewingTimesheet._dailyTotals.saturday === 0 && '-'}
                        </td>
                        <td className="p-2 text-center bg-muted/30">
                          {viewingTimesheet._dailyTotals.sunday > 0 && (
                            <span className="text-orange-600 font-bold">{viewingTimesheet._dailyTotals.sunday.toFixed(2)}</span>
                          )}
                          {viewingTimesheet._dailyTotals.sunday === 0 && '-'}
                        </td>
                        <td className="p-2 text-center font-bold text-accent-teal-900">
                          {viewingTimesheet._grandTotal.toFixed(2)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button variant="outline" onClick={() => setViewingTimesheet(null)}>
                Close
              </Button>
              {viewingTimesheet?.id && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => setShowAuditHistory(true)}
                        aria-label="View timesheet history"
                      >
                        <History className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">View History</span>
                        <span className="sm:hidden">History</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View complete audit trail and change history</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={() => {
                  if (viewingTimesheet) {
                    setRejectingTimesheet(viewingTimesheet);
                    setViewingTimesheet(null);
                  }
                }}
                disabled={!canApproveReject()}
                title={!canApproveReject() ? "You don't have permission to reject timesheets" : undefined}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={() => {
                  if (viewingTimesheet) {
                    approveTimesheet(viewingTimesheet);
                    setViewingTimesheet(null);
                  }
                }}
                disabled={!canApproveReject()}
                title={!canApproveReject() ? "You don't have permission to approve timesheets" : undefined}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectingTimesheet} onOpenChange={() => setRejectingTimesheet(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Timesheet</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting {rejectingTimesheet?.employeeName}'s timesheet
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setRejectingTimesheet(null);
              setRejectionReason('');
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={rejectTimesheet}>
              Reject Timesheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Approval Confirmation Dialog */}
      <AlertDialog open={showBulkConfirmDialog} onOpenChange={setShowBulkConfirmDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to approve {selectedTimesheets.length} timesheet{selectedTimesheets.length !== 1 ? 's' : ''}. 
              Please review the details below before proceeding.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 my-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <div className="text-sm text-muted-foreground">Total Timesheets</div>
                <div className="text-2xl font-bold">{selectedTimesheets.length}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Hours</div>
                <div className="text-2xl font-bold">
                  {selectedTimesheets.reduce((sum, ts) => sum + calculateTimesheetTotal(ts), 0).toFixed(1)}h
                </div>
              </div>
            </div>

            {/* Anomaly Warning */}
            {selectedWithAnomalies.length > 0 && (
              <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    Warning: {selectedWithAnomalies.length} Timesheet{selectedWithAnomalies.length !== 1 ? 's' : ''} with Issues
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    The following timesheets have detected anomalies. Please review them carefully:
                  </p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedWithAnomalies.slice(0, 10).map(ts => {
                      const anomalyResult = timesheetAnomalies.get(ts.id);
                      const highSeverityCount = anomalyResult?.anomalies.filter(a => a.severity === 'high').length || 0;
                      
                      return (
                        <div key={ts.id} className="flex items-center justify-between text-sm p-2 bg-background rounded">
                          <span className="font-medium">{ts.employeeName}</span>
                          <div className="flex gap-1">
                            {highSeverityCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {highSeverityCount} High
                              </Badge>
                            )}
                            <AnomalyIndicator 
                              anomalyResult={anomalyResult!}
                              size="sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                    {selectedWithAnomalies.length > 10 && (
                      <div className="text-xs text-muted-foreground text-center pt-2">
                        ...and {selectedWithAnomalies.length - 10} more
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employee List */}
            <div>
              <div className="text-sm font-medium mb-2">Affected Employees:</div>
              <div className="max-h-[150px] overflow-y-auto p-3 bg-muted/30 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {selectedTimesheets.map(ts => (
                    <div key={ts.id} className="flex items-center gap-2">
                      <Check className="h-3 w-3 text-green-600" />
                      <span>{ts.employeeName}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowBulkConfirmDialog(false);
              setPendingBulkAction(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={executeBulkApprove}>
              <Check className="h-4 w-4 mr-2" />
              Approve All {selectedTimesheets.length} Timesheets
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit History Dialog */}
      {viewingTimesheet?.id && (
        <AuditHistoryDialog
          timesheetId={viewingTimesheet.id}
          employeeName={viewingTimesheet.employeeName}
          weekEnding={viewingTimesheet.weekEnding}
          open={showAuditHistory}
          onOpenChange={setShowAuditHistory}
        />
      )}

      {/* Booking Details Modal (for variance review) */}
      {viewingBookingDetails && (
        <BookingDetailsModal
          open={!!viewingBookingDetails}
          onOpenChange={(open) => !open && setViewingBookingDetails(null)}
          booking={viewingBookingDetails}
          showVariance={true}
        />
      )}
    </div>
  );
}
